// src/components/MeetingRoom/hooks/useSeatPositions.js
import { useEffect, useMemo, useState } from "react";

export function useSeatPositions(capacity = 8) {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [smallDesk, setSmallDesk] = useState(window.innerWidth <= 1367);

    useEffect(() => {
        const onResize = () => {
            setIsMobile(window.innerWidth <= 768);
            setSmallDesk(window.innerWidth <= 1367);
        };
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);

    const seatPositions = useMemo(() => {

        /* ── 4-PERSON — SQUARE ── */
        if (capacity === 4) {
            if (isMobile) {
                return [
                    { left: "50%", top: "12%", label: "top", side: "top" },
                    { left: "82%", top: "50%", label: "bottom", side: "right" },
                    { left: "50%", top: "88%", label: "bottom", side: "bottom" },
                    { left: "18%", top: "50%", label: "top", side: "left" },
                ];
            }
            return [
                { left: "50%", top: "14%", label: "top", side: "top" },
                { left: "86%", top: "50%", label: "bottom", side: "right" },
                { left: "50%", top: "86%", label: "bottom", side: "bottom" },
                { left: "14%", top: "50%", label: "top", side: "left" },
            ];
        }

        /* ── 6-PERSON — RECTANGLE ── */
        if (capacity === 6) {
            if (isMobile) {
                /* Portrait: narrow pill, 2 seats each on L & R, heads at T & B
                   Layout:
                            [Head Top]
                      [L1]           [R1]
                      [L2]           [R2]
                           [Head Bot]
                   Array order: TL, TR, HeadR (→bottom), BR, BL, HeadL (→top)
                */
                return [
                    { left: "22%", top: "30%", label: "top", side: "left" }, // L1 (was TL)
                    { left: "78%", top: "30%", label: "top", side: "right" }, // R1 (was TR)
                    { left: "50%", top: "94%", label: "bottom", side: "bottom" }, // HeadR → Head Bot
                    { left: "78%", top: "62%", label: "bottom", side: "right" }, // R2 (was BR)
                    { left: "22%", top: "62%", label: "bottom", side: "left" }, // L2 (was BL)
                    { left: "50%", top: "6%", label: "top", side: "top" }, // HeadL → Head Top
                ];
            }
            if (smallDesk) {
                return [
                    { left: "33%", top: "12%", label: "top", side: "top" },
                    { left: "67%", top: "12%", label: "top", side: "top" },
                    { left: "92%", top: "50%", label: "bottom", side: "right" },
                    { left: "67%", top: "86%", label: "bottom", side: "bottom" },
                    { left: "33%", top: "86%", label: "bottom", side: "bottom" },
                    { left: "8%", top: "50%", label: "top", side: "left" },
                ];
            }
            return [
                { left: "33%", top: "10%", label: "top", side: "top" },
                { left: "67%", top: "10%", label: "top", side: "top" },
                { left: "93%", top: "50%", label: "bottom", side: "right" },
                { left: "67%", top: "88%", label: "bottom", side: "bottom" },
                { left: "33%", top: "88%", label: "bottom", side: "bottom" },
                { left: "7%", top: "50%", label: "top", side: "left" },
            ];
        }

        /* ── 10-PERSON — RECTANGLE ── */
        if (capacity === 10) {
            if (isMobile) {
                /* Portrait: extreme narrow pill, 4 seats each on L & R
                   Layout:
                            [Head Top]
                      [L1]           [R1]
                      [L2]           [R2]
                      [L3]           [R3]
                      [L4]           [R4]
                           [Head Bot]
                   Array order: T1..T4, HeadR (→bottom), B4..B1, HeadL (→top)
                */
                return [
                    { left: "22%", top: "18%", label: "top", side: "left" }, // L1 (was T1)
                    { left: "78%", top: "18%", label: "top", side: "right" }, // R1 (was T2)
                    { left: "22%", top: "34%", label: "top", side: "left" }, // L2 (was T3)
                    { left: "78%", top: "34%", label: "top", side: "right" }, // R2 (was T4)
                    { left: "50%", top: "94%", label: "bottom", side: "bottom" }, // HeadR → Head Bot
                    { left: "78%", top: "50%", label: "bottom", side: "right" }, // R3 (was B4)
                    { left: "22%", top: "50%", label: "bottom", side: "left" }, // L3 (was B3)
                    { left: "78%", top: "66%", label: "bottom", side: "right" }, // R4 (was B2)
                    { left: "22%", top: "66%", label: "bottom", side: "left" }, // L4 (was B1)
                    { left: "50%", top: "6%", label: "top", side: "top" }, // HeadL → Head Top
                ];
            }
            if (smallDesk) {
                return [
                    { left: "18%", top: "11%", label: "top", side: "top" },
                    { left: "34%", top: "11%", label: "top", side: "top" },
                    { left: "50%", top: "11%", label: "top", side: "top" },
                    { left: "66%", top: "11%", label: "top", side: "top" },
                    { left: "88%", top: "50%", label: "bottom", side: "right" },
                    { left: "66%", top: "87%", label: "bottom", side: "bottom" },
                    { left: "50%", top: "87%", label: "bottom", side: "bottom" },
                    { left: "34%", top: "87%", label: "bottom", side: "bottom" },
                    { left: "18%", top: "87%", label: "bottom", side: "bottom" },
                    { left: "10%", top: "50%", label: "top", side: "left" },
                ];
            }
            return [
                { left: "22%", top: "13%", label: "top", side: "top" },
                { left: "40%", top: "13%", label: "top", side: "top" },
                { left: "60%", top: "13%", label: "top", side: "top" },
                { left: "78%", top: "13%", label: "top", side: "top" },

                { left: "92%", top: "50%", label: "bottom", side: "right" },

                { left: "78%", top: "87%", label: "bottom", side: "bottom" },
                { left: "60%", top: "87%", label: "bottom", side: "bottom" },
                { left: "40%", top: "87%", label: "bottom", side: "bottom" },
                { left: "22%", top: "87%", label: "bottom", side: "bottom" },

                { left: "8%", top: "50%", label: "top", side: "left" },
            ];
        }

        /* ── Legacy 8-seat oval (unchanged) ── */
        if (isMobile) {
            return [
                { left: "32%", top: "27%", label: "top", side: "top" },
                { left: "66%", top: "27%", label: "top", side: "top" },
                { left: "82%", top: "40%", label: "top", side: "right" },
                { left: "82%", top: "56%", label: "bottom", side: "right" },
                { left: "66%", top: "73%", label: "bottom", side: "bottom" },
                { left: "32%", top: "73%", label: "bottom", side: "bottom" },
                { left: "10%", top: "56%", label: "bottom", side: "left" },
                { left: "10%", top: "40%", label: "top", side: "left" },
            ];
        }
        if (smallDesk) {
            return [
                { left: "30%", top: "22%", label: "top", side: "top" },
                { left: "60%", top: "22%", label: "top", side: "top" },
                { left: "82%", top: "36%", label: "top", side: "right" },
                { left: "82%", top: "50%", label: "bottom", side: "right" },
                { left: "30%", top: "60%", label: "bottom", side: "bottom" },
                { left: "60%", top: "60%", label: "bottom", side: "bottom" },
                { left: "7%", top: "36%", label: "top", side: "left" },
                { left: "7%", top: "50%", label: "bottom", side: "left" },
            ];
        }
        return [
            { left: "35%", top: "19%", label: "top", side: "top" },
            { left: "67%", top: "19%", label: "top", side: "top" },
            { left: "99%", top: "41%", label: "top", side: "right" },
            { left: "99%", top: "61%", label: "bottom", side: "right" },
            { left: "67%", top: "84%", label: "bottom", side: "bottom" },
            { left: "35%", top: "84%", label: "bottom", side: "bottom" },
            { left: "1%", top: "61%", label: "bottom", side: "left" },
            { left: "1%", top: "41%", label: "top", side: "left" },
        ];
    }, [isMobile, smallDesk, capacity]);

    return { isMobile, smallDesk, seatPositions };
}